// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Link, { LinkProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@tps/App/Store/Reducers";
import { maxLengthAttr } from "@tps/XDL/Utilities/validation";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";

type Props = {
    alphabet: LetterDetails[];
    prefix?: string;
    extendedStyles?: AlphabetNavigationStyles;
    onLetterClick?: (letter: string) => void;
    // XNG-Changes
    handleSearchFilterBrands?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    searchFilterBrands?: string;
    selectedLetter?: string;
};

export interface LetterDetails {
    letter: string;
    linkable: boolean;
}

export interface AlphabetNavigationStyles {
    container?: InjectableCss;
    navigationItem?: InjectableCss;
    navigationLink?: LinkProps;
    navigationTextEmpty?: TypographyPresentationProps;
    // XNG-Changes
    titleText?: TypographyPresentationProps;
    brandsLettersWrapper?: InjectableCss;
    alphabetSection?: InjectableCss;
    filterInput?: TextFieldProps;
    select?: SelectPresentationProps;
    selectWrapper?: InjectableCss;
    fullWidth?: InjectableCss;
}

export const alphabetNavigationStyles: AlphabetNavigationStyles = {
    container: {
        css: css`
            text-transform: uppercase;
            justify-items: center;
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
        `,
    },
    navigationItem: {
        css: css`
            margin: 0;
            display: inline-block;
            flex: 0 0 30px;
        `,
    },
    navigationLink: {
        css: css`
            display: block;
        `,
        typographyProps: {
            transform: "uppercase",
            variant: "headerTertiary",
            forwardAs: "span",
        },
    },
    navigationTextEmpty: {
        transform: "uppercase",
        variant: "headerTertiary",
        forwardAs: "span",
        color: "text.disabled",
    },
};

const StyledSpan = getStyledWrapper("span");

const AlphabetNavigation: FC<Props> = (props: Props) => {
    const [styles] = useState(() => mergeToNew(alphabetNavigationStyles, props.extendedStyles));
    const [alphabet] = useState(() => [{ letter: "all", count: 0, linkable: true }, ...props.alphabet]);
    const [selectedLetter, setSelectedLetter] = useState(props.selectedLetter);

    // XNG-Change
    const { isTabletScreen } = useAppSelector(state => ({
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
    }));

    // XNG-Changes: To get selectedLetter change when a user click on clean Filter
    useEffect(() => {
        setSelectedLetter(props.selectedLetter);
    }, [props.selectedLetter]);

    const handleClickOfLetter = (letter: string) => {
        props.onLetterClick?.(letter);
    };

    // XNG-Changes
    const createHandleClickOfLetter = (letter: string) => () => {
        setSelectedLetter(letter);
        return handleClickOfLetter(letter);
    };

    // XNG-Changes
    const handleSelectChange = (e: any) => {
        setSelectedLetter(e.target.value);
        return handleClickOfLetter(e.target.value);
    };

    // XNG-Changes: we add `All` as a first element
    if (alphabet.length <= 1) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container} data-test-selector="alphabetNavigation">
            <StyledWrapper {...styles.fullWidth}>
                <Typography as="div" {...styles.titleText}>
                    {isTabletScreen ? translate("Search Alphabetically") : translate("Filter Alphabetically")}
                </Typography>
                <StyledWrapper {...styles.brandsLettersWrapper}>
                    <StyledWrapper {...styles.alphabetSection}>
                        {alphabet.map((alphabetItem, index) => (
                            <StyledSpan
                                {...styles.navigationItem}
                                {...{
                                    selectedLetter: selectedLetter === alphabetItem.letter && alphabetItem.linkable,
                                    enabled: alphabetItem.linkable,
                                }}
                                className={index === alphabet.length - 1 ? "alpha-navigation-item-last-item" : ""}
                                key={alphabetItem.letter}
                                data-test-selector="alphabetNavigationItemLetter"
                            >
                                {alphabetItem.linkable ? (
                                    <Link
                                        {...styles.navigationLink}
                                        onClick={createHandleClickOfLetter(alphabetItem.letter)}
                                        aria-label={alphabetItem.letter}
                                    >
                                        {alphabetItem.letter}
                                    </Link>
                                ) : (
                                    <Typography {...styles.navigationTextEmpty}>{alphabetItem.letter}</Typography>
                                )}
                            </StyledSpan>
                        ))}
                    </StyledWrapper>
                    <StyledWrapper {...styles.selectWrapper}>
                        <Select
                            {...styles.select}
                            required
                            placeholder={translate("Search by First Letter")}
                            onChange={e => handleSelectChange(e)}
                            value={"" || selectedLetter}
                        >
                            <option disabled value="">
                                {translate("Search by First Letter")}
                            </option>
                            {alphabet.map(alphabetItem => (
                                <option key={alphabetItem.letter} value={alphabetItem.letter} disabled={!alphabetItem.linkable}>
                                    {alphabetItem.letter === "all" ? `${translate("Show All Brands")}` : alphabetItem.letter.toUpperCase()}
                                </option>
                            ))}
                        </Select>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.fullWidth}>
                <Typography as="div" {...styles.titleText}>
                    {translate("Search by Name")}
                </Typography>
                <TextField
                    name="filterBrands"
                    value={props.searchFilterBrands}
                    placeholder={translate("Search by Name")}
                    onChange={props.handleSearchFilterBrands}
                    {...styles.filterInput}
                    maxLength={maxLengthAttr.search}
                />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default AlphabetNavigation;
